import { useState, useEffect, useContext } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Box,
    Typography,
    Divider,
    Chip,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    CircularProgress,
    Alert,
    Snackbar,
    Tooltip,
    useTheme,
    TextareaAutosize,
} from "@mui/material";
import { tokens } from "../../theme";
import UserContext from "../../contexts/UserContext";
import axiosClient from "../../axios";
import isCompletedStatus from "../../requirementStatus";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { config } from "../../config";

const STATUS_OPTIONS = ["To be attended", "In progress", "Completed"];

const STATUS_COLORS = {
    "To be attended": "#858585",
    "In progress": "#6870fa",
    "Completed": "#4cceac",
    "Done": "#4cceac",
};

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (Number.isNaN(d)) return "—";
    return d.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
};

// ── Reusable left-panel detail row ────────────────────────────────────────────
const DetailRow = ({ icon, label, value, colors }) => (
    <Box mb={1.75}>
        <Box display="flex" alignItems="center" gap={0.5} mb={0.3}>
            <Box sx={{ color: colors.grey[500], display: "flex", alignItems: "center", fontSize: 13 }}>
                {icon}
            </Box>
            <Typography
                variant="caption"
                color={colors.grey[500]}
                sx={{ fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.06em" }}
            >
                {label}
            </Typography>
        </Box>
        <Typography variant="body2" color={colors.grey[100]} sx={{ ml: 0.5, wordBreak: "break-word", lineHeight: 1.5 }}>
            {value || "—"}
        </Typography>
    </Box>
);

// ── Section heading ───────────────────────────────────────────────────────────
const SectionLabel = ({ children, color, colors }) => (
    <Typography
        variant="overline"
        sx={{
            letterSpacing: "0.1em",
            display: "block",
            mb: 1.5,
            color: color || colors.grey[400],
            fontWeight: 700,
        }}
    >
        {children}
    </Typography>
);

// ── Main component ────────────────────────────────────────────────────────────
export default function BrokerInspectionDetail({ inspection, open, onClose, accentColor }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { user } = useContext(UserContext);
    const base_url = config.baseUrl.replace(/api\/?$/, "");

    const [requirements, setRequirements] = useState([]);
    const [recommendations, setRecommendations] = useState([]);
    const [loadingReqs, setLoadingReqs] = useState(false);

    // { [req.id]: "In progress" | "To be attended" | "Completed" }
    const [statusChanges, setStatusChanges] = useState({});
    // { [req.id]: FileList }
    const [fileUploads, setFileUploads] = useState({});

    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
    const [openDeleteFileDialog, setOpenDeleteFileDialog] = useState(false);
    const [deleteFileRequirement, setDeleteFileRequirement] = useState({});
    const [deleteFile, setDeleteFile] = useState({ reason: "", requirement: "" });
    const [deletingFile, setDeletingFile] = useState(false);

    // ── Fetch requirements & recommendations when dialog opens ────────────────
    useEffect(() => {
        if (!open || !inspection?.audit_id) return;
        let cancelled = false;

        const fetchDetails = async () => {
            setLoadingReqs(true);
            setStatusChanges({});
            setFileUploads({});
            try {
                const [reqRes, recRes] = await Promise.all([
                    axiosClient.get(`/inspections/requirements/${inspection.audit_id}`),
                    axiosClient.get(`/inspections/recommendations/${inspection.audit_id}`),
                ]);
                if (!cancelled) {
                    setRequirements(reqRes.data || []);
                    setRecommendations(recRes.data || []);
                }
            } catch {
                if (!cancelled) {
                    setRequirements([]);
                    setRecommendations([]);
                }
            } finally {
                if (!cancelled) setLoadingReqs(false);
            }
        };

        fetchDetails();
        return () => { cancelled = true; };
    }, [open, inspection?.audit_id]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleStatusChange = (reqId, value) =>
        setStatusChanges((prev) => ({ ...prev, [reqId]: value }));

    const handleFileChange = (reqId, files) =>
        setFileUploads((prev) => ({ ...prev, [reqId]: files }));

    const handleDeleteReqFileOpen = (req, filePath) => () => {
        const effectiveStatus = statusChanges[req.id] ?? req.requirement_status;
        if (isCompletedStatus(effectiveStatus)) {
            setSnack({
                open: true,
                message: "You can only delete files for requirements that are not completed.",
                severity: "info",
            });
            return;
        }

        setDeleteFileRequirement({
            requirement_id: req.id,
            requirement_details: req.requirement_details,
            file_path: filePath,
        });
        setDeleteFile({ reason: "", requirement: req.requirement_details });
        setOpenDeleteFileDialog(true);
    };

    const handleDeleteReqFileClose = () => {
        if (deletingFile) return;
        setOpenDeleteFileDialog(false);
    };

    const deleteRequirementFile = async () => {
        if (!deleteFile.reason.trim()) {
            setSnack({
                open: true,
                message: "Please provide a reason for deleting this file attachment.",
                severity: "error",
            });
            return;
        }

        const req = requirements.find((r) => r.id === deleteFileRequirement.requirement_id);
        if (!req) {
            setSnack({
                open: true,
                message: "Requirement not found. Please refresh and try again.",
                severity: "error",
            });
            return;
        }

        const currentPaths = (Array.isArray(req.file_path)
            ? req.file_path
            : String(req.file_path || "")
                .split(",")
                .map((s) => s.trim())
        ).filter(Boolean);

        const newPaths = currentPaths.filter((p) => p !== deleteFileRequirement.file_path);
        const effectiveStatus = statusChanges[req.id] ?? req.requirement_status;
        if (isCompletedStatus(effectiveStatus)) {
            setSnack({
                open: true,
                message: "You can only delete files for requirements that are not completed.",
                severity: "info",
            });
            return;
        }

        setDeletingFile(true);
        try {
            await axiosClient.put(`/inspections/requirements/${deleteFileRequirement.requirement_id}`, {
                file_path: newPaths.length ? newPaths.join(",") : null,
            });

            await axiosClient.post("/audit_trails", {
                user_id: String(user?.id || ""),
                insurer_id: user?.insurer_id || 0,
                inspection_id: inspection?.id,
                ip_address: "",
                title: "Requirement Attachment Deleted",
                description: `${user?.name || "Broker"} deleted an attachment from requirement (${deleteFileRequirement.requirement_details}). Reason: ${deleteFile.reason}`,
            });

            setLoadingReqs(true);
            const [reqRes, recRes] = await Promise.all([
                axiosClient.get(`/inspections/requirements/${inspection.audit_id}`),
                axiosClient.get(`/inspections/recommendations/${inspection.audit_id}`),
            ]);
            setRequirements(reqRes.data || []);
            setRecommendations(recRes.data || []);
            setOpenDeleteFileDialog(false);
            setSnack({
                open: true,
                message: "Attachment file deleted successfully.",
                severity: "success",
            });
        } catch {
            setSnack({
                open: true,
                message: "Failed to delete attachment file. Please try again.",
                severity: "error",
            });
        } finally {
            setLoadingReqs(false);
            setDeletingFile(false);
        }
    };

    const hasPendingChanges = () => Object.keys(statusChanges).length > 0;

    const handleSave = async () => {
        if (!hasPendingChanges()) {
            setSnack({ open: true, message: "No changes to save.", severity: "info" });
            return;
        }
        setSaving(true);
        try {
            const resolvedUser =
                typeof user === "string"
                    ? (() => { try { return JSON.parse(user); } catch { return {}; } })()
                    : (user || {});

            const formData = new FormData();
            formData.append("audit_id", inspection.audit_id);
            formData.append("broker_email", resolvedUser.email || "");
            formData.append("ip_address", "");
            formData.append("insurer_id", resolvedUser.insurer_id || 0);
            formData.append("html_filename", `portal_${Date.now()}.html`);

            // Send all active requirement statuses (changed + unchanged) so the
            // backend can correctly assess if all are completed.
            const activeReqs = requirements.filter(
                (r) => r.checked === 1 || r.checked === "1"
            );
            activeReqs.forEach((req) => {
                const status = statusChanges[req.id] || req.requirement_status || "";
                formData.append(`status_${req.id}`, status);

                const files = fileUploads[req.id];
                if (files && files.length > 0) {
                    Array.from(files).forEach((file) =>
                        formData.append(`filename_${req.id}_audit[]`, file)
                    );
                }
            });

            await axiosClient.post("/inspections/update_requirements", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            // Reload data so newly uploaded files and statuses are reflected immediately.
            setLoadingReqs(true);
            const [reqRes, recRes] = await Promise.all([
                axiosClient.get(`/inspections/requirements/${inspection.audit_id}`),
                axiosClient.get(`/inspections/recommendations/${inspection.audit_id}`),
            ]);
            setRequirements(reqRes.data || []);
            setRecommendations(recRes.data || []);
            setStatusChanges({});
            setFileUploads({});
            setSnack({ open: true, message: "Requirements updated successfully!", severity: "success" });
        } catch {
            setSnack({ open: true, message: "Failed to save changes. Please try again.", severity: "error" });
        } finally {
            setLoadingReqs(false);
            setSaving(false);
        }
    };

    const activeReqs = requirements.filter((r) => r.checked === 1 || r.checked === "1");
    const completedCount = activeReqs.filter(
        (r) => isCompletedStatus(statusChanges[r.id] || r.requirement_status)
    ).length;

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="xl"
                PaperProps={{
                    sx: {
                        bgcolor: colors.primary[500],
                        backgroundImage: "none",
                        maxHeight: "92vh",
                        borderTop: `3px solid ${accentColor}`,
                    },
                }}
            >
                {/* ── Title bar ── */}
                <DialogTitle
                    sx={{
                        bgcolor: colors.primary[400],
                        py: 1.5,
                        px: 2.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Box>
                        <Typography variant="h4" color={colors.grey[100]} fontWeight="bold">
                            {inspection?.audit_policy_holder || "Survey Details"}
                        </Typography>
                        <Typography variant="body2" color={colors.grey[400]} mt={0.25}>
                            Policy&nbsp;{inspection?.audit_policy_number}&ensp;·&ensp;
                            {inspection?.audit_insurer_name}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: colors.grey[300] }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                {/* ── Body ── */}
                <DialogContent sx={{ p: 0, display: "flex", overflow: "hidden" }}>
                    <Box sx={{ display: "flex", width: "100%", height: "74vh" }}>

                        {/* ━━ LEFT PANEL — Survey Information ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
                        <Box
                            sx={{
                                width: "28%",
                                minWidth: 240,
                                maxWidth: 340,
                                bgcolor: colors.primary[400],
                                borderRight: `1px solid ${colors.grey[700]}`,
                                overflowY: "auto",
                                p: 2.5,
                                flexShrink: 0,
                            }}
                        >
                            <SectionLabel color={accentColor} colors={colors}>
                                Survey Information
                            </SectionLabel>

                            <DetailRow
                                icon={<PolicyOutlinedIcon sx={{ fontSize: 14 }} />}
                                label="Policy Number"
                                value={inspection?.audit_policy_number}
                                colors={colors}
                            />
                            <DetailRow
                                icon={<BusinessOutlinedIcon sx={{ fontSize: 14 }} />}
                                label="Insurer"
                                value={inspection?.audit_insurer_name}
                                colors={colors}
                            />
                            <DetailRow
                                icon={<EmailOutlinedIcon sx={{ fontSize: 14 }} />}
                                label="Insurer Email"
                                value={inspection?.audit_insurer_email}
                                colors={colors}
                            />
                            <DetailRow
                                icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />}
                                label="Survey Date"
                                value={formatDate(inspection?.audit_survey_date)}
                                colors={colors}
                            />
                            <DetailRow
                                icon={<LocationOnOutlinedIcon sx={{ fontSize: 14 }} />}
                                label="Physical Address"
                                value={inspection?.audit_physical_address}
                                colors={colors}
                            />

                            <Divider sx={{ my: 2, borderColor: colors.grey[700] }} />
                            <SectionLabel colors={colors}>Contact Details</SectionLabel>

                            <DetailRow
                                icon={<PersonOutlinedIcon sx={{ fontSize: 14 }} />}
                                label="Contact Name"
                                value={inspection?.audit_contact_name}
                                colors={colors}
                            />
                            <DetailRow
                                icon={<PhoneOutlinedIcon sx={{ fontSize: 14 }} />}
                                label="Contact Number"
                                value={inspection?.audit_contact_number}
                                colors={colors}
                            />
                            <DetailRow
                                icon={<PersonOutlinedIcon sx={{ fontSize: 14 }} />}
                                label="Broker Name"
                                value={inspection?.audit_broker_name}
                                colors={colors}
                            />
                            <DetailRow
                                icon={<PhoneOutlinedIcon sx={{ fontSize: 14 }} />}
                                label="Broker Contact"
                                value={inspection?.audit_broker_contact_number}
                                colors={colors}
                            />

                            <Divider sx={{ my: 2, borderColor: colors.grey[700] }} />
                            <SectionLabel colors={colors}>Survey Status</SectionLabel>

                            <Box display="flex" flexWrap="wrap" gap={0.75}>
                                <Chip
                                    size="small"
                                    label={inspection?.audit_completed ? "Completed" : "In Progress"}
                                    sx={{
                                        bgcolor: inspection?.audit_completed ? "#4cceac22" : "#6870fa22",
                                        color: inspection?.audit_completed ? "#4cceac" : "#6870fa",
                                        fontWeight: 600,
                                        fontSize: "0.68rem",
                                    }}
                                />
                                {inspection?.init_email_sent ? (
                                    <Chip
                                        size="small"
                                        label="Email Sent"
                                        sx={{ bgcolor: "#4cceac22", color: "#4cceac", fontWeight: 600, fontSize: "0.68rem" }}
                                    />
                                ) : (
                                    <Chip
                                        size="small"
                                        label="Email Pending"
                                        sx={{ bgcolor: "#85858522", color: "#858585", fontWeight: 600, fontSize: "0.68rem" }}
                                    />
                                )}
                                {inspection?.escalation_status === 1 && (
                                    <Chip
                                        size="small"
                                        label="Escalated"
                                        sx={{ bgcolor: "#FF980022", color: "#FF9800", fontWeight: 600, fontSize: "0.68rem" }}
                                    />
                                )}
                                {inspection?.audit_completed_submit === 1 && (
                                    <Chip
                                        size="small"
                                        label="Submitted"
                                        sx={{ bgcolor: "#00BCD422", color: "#00BCD4", fontWeight: 600, fontSize: "0.68rem" }}
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* ━━ RIGHT PANEL — Requirements + Recommendations ━━━━━━━━━━━━━━━━━ */}
                        <Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>

                            {/* Requirements header */}
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <AssignmentTurnedInOutlinedIcon
                                        sx={{ color: accentColor, fontSize: 22 }}
                                    />
                                    <Typography variant="h5" color="#ffffff" fontWeight="bold">
                                        Requirements
                                    </Typography>
                                </Box>
                                {!loadingReqs && activeReqs.length > 0 && (
                                    <Chip
                                        label={`${completedCount} / ${activeReqs.length} completed`}
                                        sx={{
                                            bgcolor:
                                                completedCount === activeReqs.length
                                                    ? "#4cceac22"
                                                    : `${accentColor}22`,
                                            color:
                                                completedCount === activeReqs.length
                                                    ? "#4cceac"
                                                    : accentColor,
                                            fontWeight: 700,
                                            border: `1px solid ${
                                                completedCount === activeReqs.length
                                                    ? "#4cceac55"
                                                    : `${accentColor}55`
                                            }`,
                                        }}
                                    />
                                )}
                            </Box>

                            {loadingReqs && (
                                <Box display="flex" justifyContent="center" mt={6}>
                                    <CircularProgress sx={{ color: accentColor }} size={30} />
                                </Box>
                            )}

                            {!loadingReqs && activeReqs.length === 0 && (
                                <Typography variant="body2" color={colors.grey[500]} mt={3}>
                                    No active requirements found for this survey.
                                </Typography>
                            )}

                            {/* Requirement cards */}
                            {!loadingReqs &&
                                activeReqs.map((req, idx) => {
                                    const currentStatus =
                                        statusChanges[req.id] === undefined
                                            ? req.requirement_status
                                            : statusChanges[req.id];
                                    const isChanged =
                                        statusChanges[req.id] !== undefined &&
                                        statusChanges[req.id] !== req.requirement_status;
                                    const statusColor =
                                        STATUS_COLORS[currentStatus] || colors.grey[500];
                                    const isAlreadyCompleted =
                                        isCompletedStatus(req.requirement_status) &&
                                        !statusChanges[req.id];

                                    return (
                                        <Box
                                            key={req.id}
                                            sx={{
                                                mb: 2,
                                                p: 2,
                                                bgcolor: colors.primary[400],
                                                borderLeft: `3px solid ${statusColor}`,
                                                borderRadius: "0 4px 4px 0",
                                                opacity: isAlreadyCompleted ? 0.7 : 1,
                                            }}
                                        >
                                            {/* Top row: number + modified badge + status chip */}
                                            <Box
                                                display="flex"
                                                alignItems="flex-start"
                                                justifyContent="space-between"
                                                mb={1}
                                            >
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            bgcolor: `${statusColor}33`,
                                                            color: statusColor,
                                                            px: 0.75,
                                                            py: 0.2,
                                                            borderRadius: 1,
                                                            fontWeight: 700,
                                                            fontSize: "0.65rem",
                                                        }}
                                                    >
                                                        #{idx + 1}
                                                    </Typography>
                                                    {isChanged && (
                                                        <Chip
                                                            label="Modified"
                                                            size="small"
                                                            sx={{
                                                                bgcolor: "#FF980022",
                                                                color: "#FF9800",
                                                                fontWeight: 600,
                                                                fontSize: "0.62rem",
                                                                height: 18,
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                                <Chip
                                                    label={currentStatus || "—"}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: `${statusColor}22`,
                                                        color: statusColor,
                                                        fontWeight: 600,
                                                        fontSize: "0.7rem",
                                                        border: `1px solid ${statusColor}55`,
                                                    }}
                                                />
                                            </Box>

                                            {/* Requirement text */}
                                            <Typography
                                                variant="body2"
                                                color={colors.grey[100]}
                                                mb={1}
                                                sx={{ lineHeight: 1.65 }}
                                            >
                                                {req.requirement_details}
                                            </Typography>

                                            {req.expected_date && (
                                                <Box display="flex" alignItems="center" gap={0.5} mb={1.5}>
                                                    <CalendarTodayOutlinedIcon
                                                        sx={{ fontSize: 12, color: colors.grey[500] }}
                                                    />
                                                    <Typography variant="caption" color={colors.grey[500]}>
                                                        Expected by: {formatDate(req.expected_date)}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Status selector + file upload */}
                                            <Box display="flex" alignItems="flex-start" gap={2} flexWrap="wrap">
                                                <FormControl size="small" sx={{ minWidth: 190 }}>
                                                    <InputLabel
                                                        sx={{
                                                            color: colors.grey[400],
                                                            "&.Mui-focused": { color: accentColor },
                                                            fontSize: "0.8rem",
                                                        }}
                                                    >
                                                        Update Status
                                                    </InputLabel>
                                                    <Select
                                                        value={
                                                            statusChanges[req.id] === undefined
                                                                ? req.requirement_status || ""
                                                                : statusChanges[req.id]
                                                        }
                                                        label="Update Status"
                                                        onChange={(e) =>
                                                            handleStatusChange(req.id, e.target.value)
                                                        }
                                                        disabled={isAlreadyCompleted}
                                                        sx={{
                                                            color: statusColor,
                                                            bgcolor: `${statusColor}11`,
                                                            fontSize: "0.8rem",
                                                            "& .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: `${statusColor}55`,
                                                            },
                                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: statusColor,
                                                            },
                                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: statusColor,
                                                            },
                                                        }}
                                                    >
                                                        {STATUS_OPTIONS.map((s) => (
                                                            <MenuItem key={s} value={s}>
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                    <Box
                                                                        sx={{
                                                                            width: 8,
                                                                            height: 8,
                                                                            borderRadius: "50%",
                                                                            bgcolor: STATUS_COLORS[s],
                                                                            flexShrink: 0,
                                                                        }}
                                                                    />
                                                                    {s}
                                                                </Box>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>

                                                {/* File upload — shown when Completed or In progress is selected */}
                                                {(statusChanges[req.id] === "Completed" ||
                                                    statusChanges[req.id] === "In progress") && (
                                                    <Box>
                                                        <Button
                                                            component="label"
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<CloudUploadOutlinedIcon />}
                                                            sx={{
                                                                borderColor: `${accentColor}55`,
                                                                color: accentColor,
                                                                fontSize: "0.75rem",
                                                                "&:hover": {
                                                                    borderColor: accentColor,
                                                                    bgcolor: `${accentColor}11`,
                                                                },
                                                            }}
                                                        >
                                                            {fileUploads[req.id]?.length > 0
                                                                ? `${fileUploads[req.id].length} file(s) selected`
                                                                : "Attach Documents"}
                                                            <input
                                                                type="file"
                                                                hidden
                                                                multiple
                                                                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg"
                                                                onChange={(e) =>
                                                                    handleFileChange(req.id, e.target.files)
                                                                }
                                                            />
                                                        </Button>
                                                        {fileUploads[req.id]?.length > 0 && (
                                                            <Box mt={0.5}>
                                                                {Array.from(fileUploads[req.id]).map((f, i) => (
                                                                    <Typography
                                                                        key={f.name ?? String(i)}
                                                                        variant="caption"
                                                                        color={colors.grey[400]}
                                                                        display="block"
                                                                        sx={{ fontSize: "0.68rem" }}
                                                                    >
                                                                        {f.name}
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* Existing uploaded files */}
                                            {req.file_path && (() => {
                                                const paths = (Array.isArray(req.file_path)
                                                    ? req.file_path
                                                    : String(req.file_path).split(",").map((s) => s.trim())
                                                ).filter(Boolean);
                                                if (paths.length === 0) return null;
                                                return (
                                                    <Box display="flex" flexWrap="wrap" alignItems="center" gap={0.75} mt={1}>
                                                        <Typography
                                                            variant="caption"
                                                            color={colors.grey[500]}
                                                            sx={{ fontSize: "0.68rem", mr: 0.5 }}
                                                        >
                                                            Uploads:
                                                        </Typography>
                                                        {paths.map((fp) => {
                                                            const fileName = fp.split("/").pop();
                                                            return (
                                                                <Box key={fp} display="flex" alignItems="center" gap={0.5}>
                                                                    <Tooltip title={fileName} arrow>
                                                                        <a
                                                                            href={base_url + fp}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            style={{ display: "flex", alignItems: "center", color: accentColor }}
                                                                        >
                                                                            <InsertDriveFileOutlinedIcon sx={{ fontSize: 18 }} />
                                                                        </a>
                                                                    </Tooltip>
                                                                    {!isCompletedStatus(statusChanges[req.id] ?? req.requirement_status) && (
                                                                        <Tooltip title="Delete file" arrow>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={handleDeleteReqFileOpen(req, fp)}
                                                                                sx={{ color: colors.redAccent[400], p: 0.25 }}
                                                                            >
                                                                                <DeleteForeverIcon sx={{ fontSize: 16 }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                );
                                            })()}
                                        </Box>
                                    );
                                })}

                            {/* ── Recommendations ── */}
                            {!loadingReqs && recommendations.length > 0 && (
                                <>
                                    <Divider sx={{ my: 3, borderColor: colors.grey[700] }} />
                                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                                        <LightbulbOutlinedIcon
                                            sx={{ color: colors.greenAccent[500], fontSize: 22 }}
                                        />
                                        <Typography
                                            variant="h5"
                                            color="#ffffff"
                                            fontWeight="bold"
                                        >
                                            Recommendations
                                        </Typography>
                                    </Box>
                                    {recommendations.map((rec, idx) => (
                                        <Box
                                            key={rec.id || idx}
                                            sx={{
                                                mb: 1.5,
                                                p: 2,
                                                bgcolor: colors.primary[400],
                                                borderLeft: `3px solid ${colors.greenAccent[600]}`,
                                                borderRadius: "0 4px 4px 0",
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: 1.5,
                                            }}
                                        >
                                            <LightbulbOutlinedIcon
                                                sx={{
                                                    color: colors.greenAccent[500],
                                                    mt: "2px",
                                                    flexShrink: 0,
                                                    fontSize: 15,
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                color={colors.grey[100]}
                                                sx={{ lineHeight: 1.65 }}
                                            >
                                                {rec.recommendation_details || "—"}
                                            </Typography>
                                        </Box>
                                    ))}
                                </>
                            )}
                        </Box>
                    </Box>
                </DialogContent>

                {/* ── Footer ── */}
                <DialogActions
                    sx={{
                        bgcolor: colors.primary[400],
                        borderTop: `1px solid ${colors.grey[700]}`,
                        px: 2.5,
                        py: 1.5,
                        display: "flex",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography variant="body2" color={colors.grey[500]}>
                        {hasPendingChanges()
                            ? `${Object.keys(statusChanges).length} requirement(s) modified — review before saving`
                            : "Select a new status above to update requirements"}
                    </Typography>
                    <Box display="flex" gap={1.5}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            size="small"
                            sx={{ color: colors.grey[300], borderColor: colors.grey[600] }}
                        >
                            Close
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={saving || !hasPendingChanges()}
                            size="small"
                            sx={{
                                bgcolor: accentColor,
                                color: "#fff",
                                "&:hover": { bgcolor: accentColor, opacity: 0.85 },
                                "&.Mui-disabled": { bgcolor: `${accentColor}44`, color: "#ffffff88" },
                                minWidth: 130,
                            }}
                        >
                            {saving ? (
                                <CircularProgress size={16} sx={{ color: "#fff" }} />
                            ) : (
                                "Save Changes"
                            )}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={5000}
                onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snack.severity}
                    onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
                >
                    {snack.message}
                </Alert>
            </Snackbar>

            <Dialog
                open={openDeleteFileDialog}
                onClose={handleDeleteReqFileClose}
                aria-labelledby="delete-file-dialog-title"
                aria-describedby="delete-file-dialog-description"
            >
                <DialogTitle id="delete-file-dialog-title">
                    {"Are you sure you want to delete this file attachment for the following requirement?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-file-dialog-description">
                        {deleteFileRequirement.requirement_details}
                    </DialogContentText>
                    <Box mt={2}>
                        <TextareaAutosize
                            minRows={5}
                            style={{ width: "100%" }}
                            placeholder="Please provide a reason why this file is deleted."
                            required
                            value={deleteFile.reason}
                            onChange={(e) =>
                                setDeleteFile((prev) => ({
                                    ...prev,
                                    reason: e.target.value,
                                }))
                            }
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={deleteRequirementFile} disabled={deletingFile} autoFocus>
                        YES
                    </Button>
                    <Button onClick={handleDeleteReqFileClose} disabled={deletingFile}>NO</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
